/* =========================================================================
   Today's feast — first consumer of GET /api/meta/today (via new api.getToday()).
   The endpoint defaults to DateTime.UtcNow server-side, so we pass the ET date
   EXPLICITLY (the app's liturgical "today"). A 200 with a null body means no
   feast today → a bundled generic line. Offline/error → the same graceful
   fallback. Day-cached in sessionStorage so home re-renders don't re-fetch.
   Carries challengeSeed unused (v2 Daily Challenge seam).
   ========================================================================= */
import { getToday } from './api';

const CACHE_KEY = 'catholics.today.cache';
const GENERIC_LINE = 'Every day is a gift — come, let us learn a little more of the faith together.';

export interface TodayInfo {
  date: string; // ET YYYY-MM-DD
  feastTitle: string | null;
  feastLine: string;
  challengeSeed: number | null;
}

/** `YYYY-MM-DD` for the America/New_York day (en-CA formats as ISO date). */
export function etDateString(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

let memCache: TodayInfo | null = null;

export async function loadToday(now: Date = new Date()): Promise<TodayInfo> {
  const date = etDateString(now);
  if (memCache && memCache.date === date) return memCache;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (raw) {
      const cached = JSON.parse(raw) as TodayInfo;
      if (cached && cached.date === date) {
        memCache = cached;
        return cached;
      }
    }
  } catch {
    /* sessionStorage unavailable — fetch fresh */
  }

  const info: TodayInfo = { date, feastTitle: null, feastLine: GENERIC_LINE, challengeSeed: null };
  const r = await getToday(date);
  if (r.ok && r.data && r.data.title) {
    info.feastTitle = r.data.title;
    info.feastLine = `Today the Church honors ${r.data.title}.`;
    info.challengeSeed = r.data.challengeSeed ?? null;
  }

  memCache = info;
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(info));
  } catch {
    /* ignore persistence failure */
  }
  return info;
}
